import type { ReactNode } from "react";
import { Icon, type IconKey } from "@core/assets";
import { cn } from "@core/utils";

type NoticeTone = "error" | "success" | "info";

interface NoticeProps {
  tone?: NoticeTone;
  children: ReactNode;
  className?: string;
}

const TONE: Record<
  NoticeTone,
  { box: string; icon: IconKey; iconColor: string; role: "alert" | "status" }
> = {
  error: {
    box: "border-error-200 bg-error-50",
    icon: "circle-alert",
    iconColor: "text-(--feedback-error-icon)",
    role: "alert",
  },
  info: {
    box: "border-(--border-default) bg-ink-50",
    icon: "info-circle",
    iconColor: "text-(--text-secondary)",
    role: "status",
  },
  success: {
    box: "border-menthe-100 bg-menthe-50",
    icon: "circle-check",
    iconColor: "text-(--feedback-success-icon)",
    role: "status",
  },
};

/**
 * An inline message above a form (Figma "Notice"): a tinted box, an icon and one line. Used on the
 * sign-in screen for « E-mail ou mot de passe incorrect. », « Connectez-vous pour ouvrir la cohorte. » (info) and, later, for « Mot de passe modifié.
 * Connectez-vous. ». An error is announced at once (`alert`), a success politely (`status`).
 * Toasts stay for events; this is for what the person must read before acting again.
 */
export function Notice({ tone = "error", children, className }: NoticeProps) {
  const { box, icon, iconColor, role } = TONE[tone];

  return (
    <div
      role={role}
      className={cn(
        "flex items-start gap-xs rounded-md border p-sm text-sm-medium text-(--text-primary)",
        box,
        className
      )}
    >
      <Icon name={icon} size={20} className={cn("shrink-0", iconColor)} />
      <p className="min-w-0 flex-1">{children}</p>
    </div>
  );
}

export type { NoticeProps, NoticeTone };
